import { useState } from 'react'
import { Camera, QrCode } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { MobileBarcodeScannerDialog } from '@/components/orders/mobile-barcode-scanner-dialog'
import { m } from '@/lib/paraglide/messages'

interface Props {
    /**
     * Extracts a lot id (uuid) from the QR text. Return null when the QR is
     * not a lot label for this lot type, so the button can toast a helpful
     * error and keep the camera open (continuous mode).
     */
    parseLotId: (rawText: string) => string | null
    /**
     * Trả `false` (hoặc Promise ra `false`) khi lô quét được không dùng được
     * — caller tự báo lỗi, nút không báo "đã thêm". Sổ xưởng phải tra lô qua
     * API rồi mới biết (M-77).
     */
    onScanned: (lotId: string) => void | boolean | Promise<void | boolean>
    label?: string
    /** Toast khi thêm/chọn lô thành công. Mặc định "Đã thêm lô vào phiếu". */
    successMessage?: string
    /** Chỉ hiện icon camera — đặt cạnh một ô chọn lô (sổ xưởng, M-77). Nhãn
     *  vẫn là aria-label + title để đọc màn hình và tooltip. */
    iconOnly?: boolean
    className?: string
}

/**
 * Reusable "Quét mã lô" button — opens a QR scanner dialog, decodes the
 * printed tem A7 QR (which encodes `<baseURL>/.../lots/<uuid>`), and calls
 * the parent with the extracted lot id. Parent typically calls
 * `append({...})` from react-hook-form's useFieldArray.
 */
export function ScanLotCodeButton({
    parseLotId,
    onScanned,
    label,
    successMessage,
    iconOnly = false,
    className,
}: Props) {
    const btnLabel = label ?? m.lot_scan_btn_label()
    const [open, setOpen] = useState(false)
    const toastAdded = (result: void | boolean) => {
        if (result !== false)
            toast.success(successMessage ?? m.lot_scan_added_toast())
    }
    // Caller đồng bộ (phiếu kho) giữ nguyên hành vi cũ: toast ngay trong lượt
    // quét. Chỉ caller trả Promise mới phải chờ.
    const handleLotId = (lotId: string) => {
        const result = onScanned(lotId)
        if (result instanceof Promise) void result.then(toastAdded)
        else toastAdded(result)
    }
    return (
        <>
            {iconOnly ? (
                <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className={className ?? 'size-8 shrink-0'}
                    aria-label={btnLabel}
                    title={btnLabel}
                    onClick={() => setOpen(true)}
                >
                    <Camera className="size-4" />
                </Button>
            ) : (
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className={className}
                    onClick={() => setOpen(true)}
                >
                    <QrCode className="mr-1 h-4 w-4" /> {btnLabel}
                </Button>
            )}
            {open && (
                <MobileBarcodeScannerDialog
                    open={open}
                    onOpenChange={setOpen}
                    formats="qr"
                    continuous={false}
                    onRawScan={(raw) => {
                        const lotId = parseLotId(raw)
                        if (!lotId) {
                            toast.error(m.lot_scan_invalid_qr())
                            return
                        }
                        setOpen(false)
                        handleLotId(lotId)
                    }}
                />
            )}
        </>
    )
}

/** Regex used to extract the lot uuid from a scanned URL. Matches any of the
 *  4 lot-type paths from the tem A7 label QR encoded by the BE renderer (tem lô
 *  chè mua cũ không còn hợp lệ — kho đã bỏ, M-27). */
const LOT_URL_RE =
    /\/(?:sorting-warehouse\/lots|scenting-warehouse\/lots|packaging-warehouse\/lots|sales-warehouse\/lots)\/([0-9a-f-]{36})/i

/** Parses any lot URL and returns the uuid, or null if none matched. Use in
 *  ScanLotCodeButton.parseLotId. */
export function parseAnyLotIdFromQR(raw: string): string | null {
    const match = raw.trim().match(LOT_URL_RE)
    return match ? match[1] : null
}

/** Parses a specific lot type. Use this when the PXK new page only accepts
 *  one lot type, so scanning a wrong-type tem toasts an error. */
export function makeLotIdParser(
    kind:
        | 'sorting-warehouse/lots'
        | 'scenting-warehouse/lots'
        | 'packaging-warehouse/lots'
        | 'sales-warehouse/lots',
): (raw: string) => string | null {
    const re = new RegExp(`/${kind.replace(/\//g, '\\/')}/([0-9a-f-]{36})`, 'i')
    return (raw: string) => {
        const match = raw.trim().match(re)
        return match ? match[1] : null
    }
}
